import React, { useState, useEffect } from 'react';
import Card from '../../components/ui/Card';
import { useAuth } from '../../contexts/AuthContext';
import { subscribeClientsByRole } from '../../services/clientService';
import { subscribeSimulationsByRole } from '../../services/simulacaoService';
import { subscribePdfsByRole } from '../../services/PdfService';
import { useUserProfile } from '../../contexts/UserContext';

// ======== CORES OFICIAIS DOS BANCOS ========
const BANCO_CORES = {
  'Inter': '#FF500F', 'Caixa': '#005CA9', 'BRB': '#003882',
  'Bradesco': '#CC092F', 'Santander': '#EC0000', 'Itaú': '#FF6600',
  'Banco do Brasil': '#FECE00', 'Sicoob': '#003641', 'Sicredi': '#00A651',
};

function getBancoCor(nome, cor) {
  if (cor && cor !== '#666') return cor;
  const key = Object.keys(BANCO_CORES).find((k) => nome?.toLowerCase().includes(k.toLowerCase()));
  return key ? BANCO_CORES[key] : '#666';
}

function parseValor(str) {
  if (!str) return 0;
  return parseFloat(String(str).replace(/[^\d,]/g, '').replace(',', '.')) || 0;
}

function formatMoeda(v) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

function formatVolume(v) {
  if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (v >= 1_000) return `R$ ${(v / 1_000).toFixed(0)}K`;
  return `R$ ${v.toFixed(0)}`;
}

const STAGE_LABELS = {
  novo: 'Novo Lead', simulacao: 'Simulação Enviada',
  analise: 'Em Análise', fechado: 'Contrato Fechado', perdido: 'Perdido',
};
const STAGE_COLORS = {
  novo: '#3B82F6', simulacao: '#8B5CF6',
  analise: '#F5A623', fechado: '#00D68F', perdido: '#F43F5E',
};

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// ======== FUNIL DE CONVERSÃO ========
function ConversionFunnel({ clients }) {
  const stages = ['novo', 'simulacao', 'analise', 'fechado', 'perdido'];
  const counts = {};
  stages.forEach((s) => { counts[s] = clients.filter((c) => (c.stage || 'novo') === s).length; });
  const max = Math.max(...Object.values(counts), 1);

  const totalLeads = clients.length;
  const fechados = counts.fechado || 0;
  const taxaConversao = totalLeads > 0 ? ((fechados / totalLeads) * 100).toFixed(1) : '0.0';

  return (
    <Card style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>🎯 Funil de Conversão</div>
        <div style={{
          fontSize: '0.72rem', fontWeight: 700, padding: '4px 12px', borderRadius: 100,
          background: 'rgba(0,214,143,0.1)', color: '#059669',
        }}>
          Taxa: {taxaConversao}%
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {stages.filter((s) => s !== 'perdido').map((stage) => {
          const count = counts[stage];
          const pct = max > 0 ? (count / max) * 100 : 0;
          return (
            <div key={stage}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text)' }}>{STAGE_LABELS[stage]}</span>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: STAGE_COLORS[stage] }}>{count}</span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 4, width: `${pct}%`,
                  background: STAGE_COLORS[stage],
                  transition: 'width 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
                }} />
              </div>
            </div>
          );
        })}
        {counts.perdido > 0 && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', paddingTop: 8,
            borderTop: '1px solid var(--border)', marginTop: 4,
          }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Perdidos</span>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#F43F5E' }}>{counts.perdido}</span>
          </div>
        )}
      </div>
    </Card>
  );
}

// ======== TENDÊNCIA MENSAL ========
function MonthlyTrend({ simulations }) {
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ year: d.getFullYear(), month: d.getMonth(), label: MESES[d.getMonth()] });
  }

  const data = months.map((m) => {
    const count = simulations.filter((s) => {
      if (!s.criadoEm) return false;
      const d = s.criadoEm.toDate ? s.criadoEm.toDate() : new Date(s.criadoEm);
      return d.getMonth() === m.month && d.getFullYear() === m.year;
    }).length;
    return { ...m, count };
  });

  const maxCount = Math.max(...data.map((d) => d.count), 1);

  return (
    <Card style={{ padding: 20 }}>
      <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 20 }}>📈 Simulações por Mês</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 140 }}>
        {data.map((d, i) => {
          const pct = (d.count / maxCount) * 100;
          const isCurrentMonth = i === data.length - 1;
          return (
            <div key={d.label + d.year} style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 6, height: '100%', justifyContent: 'flex-end',
            }}>
              <span style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text)' }}>
                {d.count || ''}
              </span>
              <div style={{
                width: '100%', height: `${Math.max(pct, 4)}%`,
                background: isCurrentMonth ? 'var(--accent)' : '#3B82F6',
                borderRadius: '6px 6px 2px 2px', minHeight: 4,
                transition: 'height 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
                opacity: isCurrentMonth ? 1 : 0.7,
              }} />
              <span style={{
                fontSize: '0.62rem', fontWeight: 600, textAlign: 'center',
                color: isCurrentMonth ? 'var(--accent)' : 'var(--text-muted)',
              }}>
                {d.label}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ======== RANKING DE BANCOS ========
function BankRanking({ simulations }) {
  const bancoStats = {};
  simulations.forEach((sim) => {
    const melhor = sim.melhorBanco;
    if (melhor?.nome) {
      if (!bancoStats[melhor.nome]) bancoStats[melhor.nome] = { nome: melhor.nome, cor: getBancoCor(melhor.nome, melhor.cor), vezes: 0 };
      bancoStats[melhor.nome].vezes++;
    }
  });

  const ranking = Object.values(bancoStats).sort((a, b) => b.vezes - a.vezes);
  const totalMelhor = ranking.reduce((s, r) => s + r.vezes, 0);

  if (ranking.length === 0) return (
    <Card style={{ padding: 20 }}>
      <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 16 }}>🏆 Ranking — Melhor Banco</div>
      <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', textAlign: 'center', padding: 30 }}>
        Dados aparecerão após simulações
      </div>
    </Card>
  );

  return (
    <Card style={{ padding: 20 }}>
      <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 16 }}>🏆 Ranking — Melhor Banco</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {ranking.map((b, i) => {
          const pct = totalMelhor > 0 ? (b.vezes / totalMelhor) * 100 : 0;
          return (
            <div key={b.nome}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.6rem', fontWeight: 700, color: '#fff', background: b.cor,
                  }}>{i + 1}</span>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>{b.nome}</span>
                </div>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: b.cor }}>
                  {b.vezes}x ({pct.toFixed(0)}%)
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 3, width: `${pct}%`, background: b.cor,
                  transition: 'width 0.6s ease',
                }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ======== TOP CLIENTES ========
function TopClients({ clients }) {
  const top = [...clients]
    .filter((c) => c.simulacoes > 0)
    .sort((a, b) => parseValor(b.valorFinanciado) - parseValor(a.valorFinanciado))
    .slice(0, 5);

  const COLORS = ['#3B82F6', '#8B5CF6', '#14B8A6', '#F43F5E', '#F97316'];

  if (top.length === 0) return (
    <Card style={{ padding: 20 }}>
      <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 16 }}>👥 Top Clientes por Volume</div>
      <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', textAlign: 'center', padding: 30 }}>
        Dados aparecerão após simulações com clientes
      </div>
    </Card>
  );

  return (
    <Card style={{ padding: 20 }}>
      <div style={{ fontSize: '0.82rem', fontWeight: 700, marginBottom: 16 }}>👥 Top Clientes por Volume</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {top.map((c, i) => {
          const iniciais = (c.nome || 'U').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
          const valor = parseValor(c.valorFinanciado);
          const stage = c.stage || 'novo';
          return (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.65rem', fontWeight: 700, color: '#fff', background: COLORS[i % COLORS.length], flexShrink: 0,
              }}>{iniciais}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>{c.nome}</span>
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoeda(valor)}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                  <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                    {c.simulacoes || 0} simulaç{(c.simulacoes || 0) === 1 ? 'ão' : 'ões'}
                  </span>
                  <span style={{
                    fontSize: '0.58rem', fontWeight: 600, padding: '2px 6px', borderRadius: 100,
                    background: `${STAGE_COLORS[stage]}18`, color: STAGE_COLORS[stage],
                  }}>{STAGE_LABELS[stage]}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ======== MÉTRICAS RÁPIDAS ========
function QuickMetrics({ clients, simulations, pdfs }) {
  const fechados = clients.filter((c) => c.stage === 'fechado');
  const volumeFechado = fechados.reduce((s, c) => s + parseValor(c.valorFinanciado), 0);

  const avgSimPorCliente = clients.length > 0
    ? (simulations.filter((s) => s.clientId).length / clients.length).toFixed(1)
    : '0';

  const totalSimulacoes = simulations.length;
  const comCliente = simulations.filter((s) => s.clientId).length;
  const taxaVinculacao = totalSimulacoes > 0 ? ((comCliente / totalSimulacoes) * 100).toFixed(0) : '0';

  const metrics = [
    { label: 'Volume Fechado', value: formatVolume(volumeFechado), cor: '#059669' },
    { label: 'Média Sim/Cliente', value: avgSimPorCliente, cor: '#3B82F6' },
    { label: 'Taxa Vinculação', value: `${taxaVinculacao}%`, cor: '#8B5CF6' },
    { label: 'PDFs Gerados', value: pdfs.length.toString(), cor: '#F5A623' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
      {metrics.map((m) => (
        <Card key={m.label} style={{ padding: '16px 18px' }}>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: m.cor, letterSpacing: '-0.02em' }}>{m.value}</div>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 2 }}>{m.label}</div>
        </Card>
      ))}
    </div>
  );
}

// ======== PAGE ========
export default function PerformancePage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [pdfs, setPdfs] = useState([]);
const { profile } = useUserProfile();
 useEffect(() => {
  if (!profile) return;
  const unsub1 = subscribeClientsByRole(profile, setClients);
  const unsub2 = subscribeSimulationsByRole(profile, setSimulations);
  const unsub3 = subscribePdfsByRole(profile, setPdfs);
  return () => { unsub1(); unsub2(); unsub3(); };
}, [profile]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Métricas rápidas */}
      <QuickMetrics clients={clients} simulations={simulations} pdfs={pdfs} />

      {/* Funil + Tendência */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <ConversionFunnel clients={clients} />
        <MonthlyTrend simulations={simulations} />
      </div>

      {/* Ranking + Top Clientes */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <BankRanking simulations={simulations} />
        <TopClients clients={clients} />
      </div>
    </div>
  );
}